// 🔥 가격 Diff 미리보기 컴포넌트 - 프리미엄급 UI
// [배경] 현재 데이터와 스냅샷 데이터의 변경사항을 시각적으로 비교 표시
// 복구 마법사의 Step 3에서 사용

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Collapse,
  Alert,
  AlertTitle,
  LinearProgress,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
  CheckCircle as UnchangedIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Smartphone as SmartphoneIcon,
  Tablet as TabletIcon,
  Headphones as HeadphonesIcon
} from '@mui/icons-material';

import {
  PriceDiffResult,
  PriceDiffItem,
  DiffSummary,
  DiffType
} from '../../../../types/priceSnapshot';
import { DeviceType, DeviceBrand } from '../../../../types/deviceCatalog';

// ============================================================
// 📊 타입 정의
// ============================================================

interface PriceDiffPreviewProps {
  /** Diff 결과 데이터 */
  diffResult: PriceDiffResult;
  /** 로딩 상태 */
  loading?: boolean;
  /** 최대 표시 행 수 (기본: 100) */
  maxRows?: number;
  /** 컴팩트 모드 */
  compact?: boolean;
}

type FilterDiffType = DiffType | 'all';

// ============================================================
// 🎨 스타일 상수
// ============================================================

const DIFF_COLORS = {
  added: {
    bg: '#dcfce7',     // 연한 녹색
    text: '#166534',   // 진한 녹색
    border: '#86efac'
  },
  removed: {
    bg: '#fee2e2',     // 연한 빨간색
    text: '#991b1b',   // 진한 빨간색
    border: '#fca5a5'
  },
  modified: {
    bg: '#fef3c7',     // 연한 노란색
    text: '#92400e',   // 진한 갈색
    border: '#fcd34d'
  },
  unchanged: {
    bg: '#f3f4f6',     // 연한 회색
    text: '#6b7280',   // 회색
    border: '#d1d5db'
  }
};

const BRAND_COLORS = {
  Apple: '#555555',
  Samsung: '#1428a0'
};

// ============================================================
// 🔧 유틸리티 함수
// ============================================================

/**
 * Diff 타입에 따른 아이콘
 */
const getDiffIcon = (type: DiffType) => {
  switch (type) {
    case 'added':
      return <AddIcon fontSize="small" sx={{ color: DIFF_COLORS.added.text }} />;
    case 'removed':
      return <RemoveIcon fontSize="small" sx={{ color: DIFF_COLORS.removed.text }} />;
    case 'modified':
      return <EditIcon fontSize="small" sx={{ color: DIFF_COLORS.modified.text }} />;
    case 'unchanged':
      return <UnchangedIcon fontSize="small" sx={{ color: DIFF_COLORS.unchanged.text }} />;
  }
};

/**
 * Diff 타입에 따른 라벨
 */
const getDiffLabel = (type: DiffType): string => {
  switch (type) {
    case 'added': return '복구 시 추가';
    case 'removed': return '복구 시 삭제';
    case 'modified': return '가격 변경';
    case 'unchanged': return '변경 없음';
  }
};

/**
 * 디바이스 타입 아이콘
 */
const getDeviceIcon = (type: DeviceType) => {
  switch (type) {
    case 'smartphone':
      return <SmartphoneIcon fontSize="small" />;
    case 'tablet':
      return <TabletIcon fontSize="small" />;
    case 'wearable':
      return <HeadphonesIcon fontSize="small" />;
  }
};

/**
 * 가격 변경 트렌드 아이콘
 */
const getTrendIcon = (difference: number) => {
  if (difference > 0) {
    return <TrendingUpIcon fontSize="small" sx={{ color: '#10b981' }} />;
  } else if (difference < 0) {
    return <TrendingDownIcon fontSize="small" sx={{ color: '#ef4444' }} />;
  }
  return <TrendingFlatIcon fontSize="small" sx={{ color: '#6b7280' }} />;
};

/**
 * 가격 포맷팅
 */
const formatPrice = (price: number | undefined): string => {
  if (price === undefined || price === null) return '-';
  return price.toLocaleString() + '원';
};

// ============================================================
// 📦 서브 컴포넌트: 요약 카드
// ============================================================

interface SummaryCardsProps {
  summary: DiffSummary;
}

const SummaryCards: React.FC<SummaryCardsProps> = ({ summary }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6} sm={3}>
        <Card sx={{ bgcolor: DIFF_COLORS.added.bg, border: `1px solid ${DIFF_COLORS.added.border}` }}>
          <CardContent sx={{ textAlign: 'center', py: isMobile ? 1.5 : 2 }}>
            <AddIcon sx={{ color: DIFF_COLORS.added.text, fontSize: 28 }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: DIFF_COLORS.added.text }}>
              {summary.added}
            </Typography>
            <Typography variant="caption" sx={{ color: DIFF_COLORS.added.text }}>
              복구 시 추가
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={3}>
        <Card sx={{ bgcolor: DIFF_COLORS.removed.bg, border: `1px solid ${DIFF_COLORS.removed.border}` }}>
          <CardContent sx={{ textAlign: 'center', py: isMobile ? 1.5 : 2 }}>
            <RemoveIcon sx={{ color: DIFF_COLORS.removed.text, fontSize: 28 }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: DIFF_COLORS.removed.text }}>
              {summary.removed}
            </Typography>
            <Typography variant="caption" sx={{ color: DIFF_COLORS.removed.text }}>
              복구 시 삭제
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={3}>
        <Card sx={{ bgcolor: DIFF_COLORS.modified.bg, border: `1px solid ${DIFF_COLORS.modified.border}` }}>
          <CardContent sx={{ textAlign: 'center', py: isMobile ? 1.5 : 2 }}>
            <EditIcon sx={{ color: DIFF_COLORS.modified.text, fontSize: 28 }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: DIFF_COLORS.modified.text }}>
              {summary.modified}
            </Typography>
            <Typography variant="caption" sx={{ color: DIFF_COLORS.modified.text }}>
              가격 변경
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={3}>
        <Card sx={{ bgcolor: DIFF_COLORS.unchanged.bg, border: `1px solid ${DIFF_COLORS.unchanged.border}` }}>
          <CardContent sx={{ textAlign: 'center', py: isMobile ? 1.5 : 2 }}>
            <UnchangedIcon sx={{ color: DIFF_COLORS.unchanged.text, fontSize: 28 }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: DIFF_COLORS.unchanged.text }}>
              {summary.unchanged}
            </Typography>
            <Typography variant="caption" sx={{ color: DIFF_COLORS.unchanged.text }}>
              변경 없음
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// ============================================================
// 📦 서브 컴포넌트: 가격 변경 셀
// ============================================================

interface PriceChangeCellProps {
  item: PriceDiffItem;
  grade: 'A' | 'B' | 'C' | 'D';
}

const PriceChangeCell: React.FC<PriceChangeCellProps> = ({ item, grade }) => {
  const currentPrice = item.currentPrices?.[grade];
  const snapshotPrice = item.snapshotPrices?.[grade];

  if (item.diffType === 'unchanged') {
    return (
      <Typography variant="body2" color="text.secondary">
        {formatPrice(currentPrice)}
      </Typography>
    );
  }

  if (item.diffType === 'added') {
    return (
      <Typography variant="body2" sx={{ color: DIFF_COLORS.added.text }}>
        → {formatPrice(snapshotPrice)}
      </Typography>
    );
  }

  if (item.diffType === 'removed') {
    return (
      <Typography variant="body2" sx={{ color: DIFF_COLORS.removed.text, textDecoration: 'line-through' }}>
        {formatPrice(currentPrice)}
      </Typography>
    );
  }

  // modified
  const priceChange = item.priceChanges?.find(c => c.grade === grade);
  if (!priceChange || priceChange.difference === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {formatPrice(currentPrice)}
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          variant="body2"
          sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
        >
          {formatPrice(priceChange.currentValue)}
        </Typography>
        {getTrendIcon(priceChange.snapshotValue - priceChange.currentValue)}
      </Stack>
      <Typography
        variant="body2"
        fontWeight="bold"
        sx={{ color: DIFF_COLORS.modified.text }}
      >
        → {formatPrice(priceChange.snapshotValue)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        ({priceChange.difference > 0 ? '+' : ''}{priceChange.difference.toLocaleString()})
      </Typography>
    </Box>
  );
};

// ============================================================
// 📦 메인 컴포넌트
// ============================================================

const PriceDiffPreview: React.FC<PriceDiffPreviewProps> = ({
  diffResult,
  loading = false,
  maxRows = 100,
  compact = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 필터 상태
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterDiffType>('all');
  const [filterBrand, setFilterBrand] = useState<DeviceBrand | 'all'>('all');
  const [filterDeviceType, setFilterDeviceType] = useState<DeviceType | 'all'>('all');
  const [showUnchanged, setShowUnchanged] = useState(false);

  // 확장된 행
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // 필터링된 아이템
  const filteredItems = useMemo(() => {
    let items = diffResult.items;

    // 변경 없음 필터
    if (!showUnchanged) {
      items = items.filter(item => item.diffType !== 'unchanged');
    }

    // 검색 필터
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item =>
        item.modelName.toLowerCase().includes(query) ||
        item.modelId.toLowerCase().includes(query) ||
        item.storage.toLowerCase().includes(query)
      );
    }

    // 타입 필터
    if (filterType !== 'all') {
      items = items.filter(item => item.diffType === filterType);
    }

    // 브랜드 필터
    if (filterBrand !== 'all') {
      items = items.filter(item => item.brand === filterBrand);
    }

    // 디바이스 타입 필터
    if (filterDeviceType !== 'all') {
      items = items.filter(item => item.deviceType === filterDeviceType);
    }

    return items.slice(0, maxRows);
  }, [diffResult.items, searchQuery, filterType, filterBrand, filterDeviceType, showUnchanged, maxRows]);

  // 행 확장 토글
  const toggleRow = (priceId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(priceId)) {
        next.delete(priceId);
      } else {
        next.add(priceId);
      }
      return next;
    });
  };

  // 로딩 상태
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography align="center" color="text.secondary">
          변경사항 비교 중...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* 요약 카드 */}
      {!compact && <SummaryCards summary={diffResult.summary} />}

      {/* 알림: 많은 변경사항 */}
      {diffResult.summary.modified + diffResult.summary.added + diffResult.summary.removed > 50 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>변경사항 많음</AlertTitle>
          총 {diffResult.summary.modified + diffResult.summary.added + diffResult.summary.removed}개의 변경사항이 있습니다.
          필터를 사용하여 원하는 항목을 찾아보세요.
        </Alert>
      )}

      {/* 필터 바 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* 검색 */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="모델명, ID 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {/* 변경 유형 필터 */}
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>변경 유형</InputLabel>
              <Select
                value={filterType}
                label="변경 유형"
                onChange={(e) => setFilterType(e.target.value as FilterDiffType)}
              >
                <MenuItem value="all">전체</MenuItem>
                <MenuItem value="added">추가</MenuItem>
                <MenuItem value="removed">삭제</MenuItem>
                <MenuItem value="modified">변경</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* 브랜드 필터 */}
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>브랜드</InputLabel>
              <Select
                value={filterBrand}
                label="브랜드"
                onChange={(e) => setFilterBrand(e.target.value as DeviceBrand | 'all')}
              >
                <MenuItem value="all">전체</MenuItem>
                <MenuItem value="Apple">Apple</MenuItem>
                <MenuItem value="Samsung">Samsung</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* 디바이스 타입 필터 */}
          {!isSmallMobile && (
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>디바이스</InputLabel>
                <Select
                  value={filterDeviceType}
                  label="디바이스"
                  onChange={(e) => setFilterDeviceType(e.target.value as DeviceType | 'all')}
                >
                  <MenuItem value="all">전체</MenuItem>
                  <MenuItem value="smartphone">스마트폰</MenuItem>
                  <MenuItem value="tablet">태블릿</MenuItem>
                  <MenuItem value="wearable">웨어러블</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* 변경 없음 표시 토글 */}
          <Grid item xs={12} sm={6} md={3}>
            <Chip
              label={showUnchanged ? '변경 없음 숨기기' : '변경 없음 보기'}
              onClick={() => setShowUnchanged(!showUnchanged)}
              color={showUnchanged ? 'primary' : 'default'}
              variant={showUnchanged ? 'filled' : 'outlined'}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* 결과 정보 */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {filteredItems.length}개 항목 표시
        {filteredItems.length >= maxRows && ` (최대 ${maxRows}개)`}
        {' · '}처리 시간: {diffResult.processingTimeMs}ms
      </Typography>

      {/* 테이블 */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: compact ? 400 : 500,
          '& .MuiTableCell-root': {
            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
            py: compact ? 0.5 : 1
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell width={32}></TableCell>
              <TableCell>모델</TableCell>
              <TableCell>상태</TableCell>
              {!isSmallMobile && (
                <>
                  <TableCell align="center">A등급</TableCell>
                  <TableCell align="center">B등급</TableCell>
                  <TableCell align="center">C등급</TableCell>
                  <TableCell align="center">D등급</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    표시할 변경사항이 없습니다.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => {
                const isExpanded = expandedRows.has(item.priceId);
                const rowBgColor = DIFF_COLORS[item.diffType].bg;

                return (
                  <React.Fragment key={item.priceId}>
                    <TableRow
                      hover
                      sx={{
                        bgcolor: alpha(rowBgColor, 0.5),
                        '&:hover': { bgcolor: alpha(rowBgColor, 0.8) }
                      }}
                    >
                      {/* 확장 버튼 */}
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleRow(item.priceId)}>
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>

                      {/* 모델 정보 */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Tooltip title={item.deviceType}>
                            {getDeviceIcon(item.deviceType)}
                          </Tooltip>
                          <Box>
                            <Typography variant="body2" fontWeight="medium" noWrap>
                              {item.modelName}
                            </Typography>
                            <Stack direction="row" spacing={0.5}>
                              <Chip
                                size="small"
                                label={item.brand}
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: alpha(BRAND_COLORS[item.brand], 0.1),
                                  color: BRAND_COLORS[item.brand]
                                }}
                              />
                              <Chip
                                size="small"
                                label={item.storage}
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                              {item.connectivity && (
                                <Chip
                                  size="small"
                                  label={item.connectivity}
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* 상태 */}
                      <TableCell>
                        <Chip
                          size="small"
                          icon={getDiffIcon(item.diffType)}
                          label={getDiffLabel(item.diffType)}
                          sx={{
                            bgcolor: DIFF_COLORS[item.diffType].bg,
                            color: DIFF_COLORS[item.diffType].text,
                            border: `1px solid ${DIFF_COLORS[item.diffType].border}`
                          }}
                        />
                      </TableCell>

                      {/* 등급별 가격 (데스크탑) */}
                      {!isSmallMobile && (
                        <>
                          <TableCell align="center">
                            <PriceChangeCell item={item} grade="A" />
                          </TableCell>
                          <TableCell align="center">
                            <PriceChangeCell item={item} grade="B" />
                          </TableCell>
                          <TableCell align="center">
                            <PriceChangeCell item={item} grade="C" />
                          </TableCell>
                          <TableCell align="center">
                            <PriceChangeCell item={item} grade="D" />
                          </TableCell>
                        </>
                      )}
                    </TableRow>

                    {/* 확장 영역 (모바일 가격 상세 + 상세 정보) */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 1, bgcolor: 'grey.50' }}>
                            {/* 모바일용 가격 표시 */}
                            {isSmallMobile && (
                              <Grid container spacing={1} sx={{ mb: 2 }}>
                                {(['A', 'B', 'C', 'D'] as const).map(grade => (
                                  <Grid item xs={6} key={grade}>
                                    <Paper sx={{ p: 1, textAlign: 'center' }}>
                                      <Typography variant="caption" fontWeight="bold">
                                        {grade}등급
                                      </Typography>
                                      <PriceChangeCell item={item} grade={grade} />
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            )}

                            {/* 상세 정보 */}
                            <Typography variant="caption" color="text.secondary">
                              Price ID: {item.priceId}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              Model ID: {item.modelId}
                            </Typography>

                            {/* 변경 요약 (modified인 경우) */}
                            {item.diffType === 'modified' && item.priceChanges && item.priceChanges.length > 0 && (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                  {item.priceChanges.length}개 등급 가격 변경:
                                  {item.priceChanges.map(c => (
                                    <span key={c.grade}>
                                      {' '}{c.grade}등급 ({c.difference > 0 ? '+' : ''}{c.difference.toLocaleString()}원)
                                    </span>
                                  ))}
                                </Typography>
                              </Alert>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PriceDiffPreview;

